import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User2Icon, Building, FileArchive, X, Delete, Mail, Eye } from 'lucide-react';
import StatusChip from '../StatusChip';

const UserDialog = ({ 
  userDialogOpen, 
  setUserDialogOpen, 
  selectedUser,
  editUserData,
  setEditUserData,
  handleSaveUser,
  setDeleteUserDialogOpen,
  setEmailDialogOpen,
  userReports,
  userProperties,
  handleViewReport,
  handleViewProperty
}) => {
  return (
    <AnimatePresence>
      {userDialogOpen && (
        <motion.div
          className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setUserDialogOpen(false)} />
          
          <motion.div
            className="relative bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-screen overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 30 }}
          >
            <div className="px-6 py-4 bg-indigo-600 text-white flex justify-between items-center">
              <h3 className="text-lg font-medium">
                Detalles del Usuario: {selectedUser?.nombre}
              </h3>
              <button 
                onClick={() => setUserDialogOpen(false)}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {selectedUser && (
              <>
                <div className="p-6 max-h-[80vh] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* User information */}
                    <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                      <h4 className="text-md font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                        <User2Icon className="w-4 h-4" />
                        Información Personal
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-medium text-gray-500">ID</p>
                          <p className="text-sm text-gray-700">{selectedUser.id}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Nombre</p>
                          <input
                            type="text"
                            value={editUserData.nombre || ''}
                            onChange={(e) => setEditUserData({...editUserData, nombre: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                    focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Email</p>
                          <input
                            type="email"
                            value={editUserData.email || ''}
                            onChange={(e) => setEditUserData({...editUserData, email: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                    focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Teléfono</p>
                          <input
                            type="text"
                            value={editUserData.telefono || ''}
                            onChange={(e) => setEditUserData({...editUserData, telefono: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                    focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Tipo de Usuario</p>
                          <select
                            value={editUserData.tipo_usuario_id || ''}
                            onChange={(e) => setEditUserData({...editUserData, tipo_usuario_id: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                    focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value={1}>Cliente</option>
                            <option value={2}>Vendedor</option>
                            <option value={3}>Constructora</option>
                            <option value={4}>Inmobiliaria</option>
                            <option value={5}>Staff</option>
                          </select>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Estado</p>
                          <select
                            value={editUserData.estado || ''}
                            onChange={(e) => setEditUserData({...editUserData, estado: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                    focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="activo">Activo</option>
                            <option value="suspendido">Suspendido</option>
                            <option value="inactivo">Inactivo</option>
                          </select>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Plan</p>
                          <select
                            value={editUserData.plan || ''}
                            onChange={(e) => setEditUserData({...editUserData, plan: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                    focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">Sin plan</option>
                            <option value="basic">Básico</option>
                            <option value="premium">Premium</option>
                            <option value="professional">Profesional</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between">
                        <motion.button
                          onClick={() => setDeleteUserDialogOpen(true)}
                          className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Delete className="h-4 w-4 mr-1" />
                          Eliminar
                        </motion.button>
                        
                        <motion.button
                          onClick={() => setEmailDialogOpen(true)}
                          className="inline-flex items-center px-3 py-2 border border-indigo-300 text-sm leading-4 font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Enviar Email
                        </motion.button>
                      </div>
                    </div>
                    
                    {/* User properties */}
                    <div className="col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm">
                      <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                        <h4 className="text-md font-semibold text-indigo-800 flex items-center gap-2">
                          <Building className="w-4 h-4" />
                          Propiedades del Usuario
                        </h4>
                        <span className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full">
                          {userProperties.length} propiedades
                        </span>
                      </div>
                      
                      <div className="overflow-y-auto" style={{ maxHeight: '300px' }}>
                        {userProperties.length > 0 ? (
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Título</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {userProperties.map((property) => (
                                <tr key={property.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{property.id}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{property.titulo}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{property.tipo}</td>
                                  <td className="px-6 py-4 whitespace-nowrap"><StatusChip status={property.estado} /></td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button
                                      onClick={() => handleViewProperty(property)}
                                      className="text-indigo-600 hover:text-indigo-900"
                                    >
                                      <Eye className="w-4 h-4 inline mr-1" />
                                      Ver
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="py-8 text-center text-gray-500">
                            El usuario no tiene propiedades
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* User reports */}
                    <div className="col-span-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                      <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                        <h4 className="text-md font-semibold text-indigo-800 flex items-center gap-2">
                          <FileArchive className="w-4 h-4" />
                          Reportes Realizados
                        </h4>
                        <span className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full">
                          {userReports.length} reportes
                        </span>
                      </div>
                      
                      <div className="overflow-y-auto" style={{ maxHeight: '300px' }}>
                        {userReports.length > 0 ? (
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Propiedad</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {userReports.map((report) => (
                                <tr key={report.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{report.id}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{report.propiedad_id}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{report.tipo_reporte}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(report.fecha_reporte).toLocaleString()}</td>
                                  <td className="px-6 py-4 whitespace-nowrap"><StatusChip status={report.estado} /></td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button
                                      onClick={() => handleViewReport(report)}
                                      className="text-indigo-600 hover:text-indigo-900"
                                    >
                                      <Eye className="w-4 h-4 inline mr-1" />
                                      Ver
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="py-8 text-center text-gray-500">
                            El usuario no ha realizado reportes
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                  <motion.button
                    onClick={() => setUserDialogOpen(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancelar
                  </motion.button>
                  
                  <motion.button
                    onClick={handleSaveUser}
                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg shadow-md hover:shadow-lg"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Guardar Cambios
                  </motion.button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UserDialog;